import {
  LoadStopTypes,
  LoadStopsItem,
  LoadStopsItemTypes,
  NormalizedDropoff,
  NormalizedLoad,
  NormalizedPickup,
  createInitLoad,
  normalizeLoad,
} from 'types/Load';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import { validateDeliveryOnOrAfterPickupDate } from '../utils/stopValidation';

function toDateString(value: unknown): string {
  if (!value) {
    return '';
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return String(value);
}

export function toMaybeDate(value: unknown): Maybe<Date> {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function defaultTurvoPickupConsignee(): Pick<
  NormalizedLoad,
  'pickup' | 'consignee'
> {
  const base = normalizeLoad(TMS.Turvo, createInitLoad());

  return {
    pickup: base.pickup,
    consignee: base.consignee,
  };
}

/** Soft cap aligned with other route UIs; Turvo has no hard documented max. */
export const MAX_TURVO_ROUTE_STOPS = 12;

export function buildTurvoStopsFromPickupAndConsignee(
  pickup: MaybeUndef<NormalizedPickup>,
  consignee: MaybeUndef<NormalizedDropoff>
): LoadStopsItem[] {
  const defaultStops = createInitLoad().stops!;

  return [
    buildTurvoRouteStopFromFlatLocation(
      0,
      pickup,
      LoadStopsItemTypes.Pickup,
      defaultStops[0]
    ),
    buildTurvoRouteStopFromFlatLocation(
      1,
      consignee,
      LoadStopsItemTypes.Dropoff,
      defaultStops[1]
    ),
  ];
}

function buildTurvoRouteStopFromFlatLocation(
  index: number,
  location: MaybeUndef<NormalizedPickup | NormalizedDropoff>,
  stopType: LoadStopsItemTypes,
  fallback: LoadStopsItem
): LoadStopsItem {
  if (!location) {
    return {
      ...fallback,
      order: index,
      stopNumber: index,
      stopType,
    };
  }

  const isDropoff = stopType === LoadStopsItemTypes.Dropoff;

  return {
    ...fallback,
    order: index,
    stopNumber: index,
    stopType,
    externalTMSID: location.externalTMSID ?? '',
    contact: location.contact ?? '',
    phone: location.phone ?? '',
    email: location.email ?? '',
    refNumber: location.refNumber ?? '',
    apptRequired: location.apptRequired ?? false,
    apptType: location.apptType ?? '',
    apptStartTime: toDateString(location.apptStartTime),
    apptEndTime: toDateString(location.apptEndTime),
    apptNum: location.apptNum ?? '',
    apptNote: location.apptNote ?? '',
    timezone: location.timezone ?? '',
    additionalReferences: location.additionalReferences ?? [],
    // Pickup windows live on readyTime; delivery windows on mustDeliver.
    // Filling the other field lets a consignee carry a pickup time into Turvo.
    readyTime: toDateString(
      isDropoff
        ? ''
        : ((location as NormalizedPickup).readyTime ?? location.apptStartTime)
    ),
    readyEndTime: '',
    mustDeliver: toDateString(
      isDropoff
        ? ((location as NormalizedDropoff).mustDeliver ??
            location.apptStartTime)
        : ''
    ),
    address: {
      name: location.name ?? '',
      addressLine1: location.addressLine1 ?? '',
      addressLine2: location.addressLine2 ?? '',
      city: location.city ?? '',
      state: location.state ?? '',
      zip: location.zipCode ?? '',
      country: location.country ?? '',
      timezone: location.timezone ?? '',
      isVerified: false,
    },
  };
}

export function isTurvoPickupStopType(stopType: string | undefined): boolean {
  const normalized = String(stopType ?? '')
    .trim()
    .toLowerCase();
  return (
    normalized === LoadStopsItemTypes.Pickup ||
    normalized === LoadStopsItemTypes.Stop
  );
}

export function isTurvoDropoffStopType(stopType: string | undefined): boolean {
  const normalized = String(stopType ?? '')
    .trim()
    .toLowerCase();
  return (
    normalized === LoadStopsItemTypes.Dropoff ||
    normalized === LoadStopsItemTypes.Delivery
  );
}

export function createTurvoIntermediateStop(order: number): LoadStopsItem {
  const template = createInitLoad().stops![1];

  return {
    ...template,
    order,
    stopNumber: order,
    stopType: LoadStopsItemTypes.Pickup,
    refNumber: '',
    readyTime: '',
    readyEndTime: '',
    mustDeliver: '',
    apptType: '',
    apptStartTime: '',
    apptEndTime: '',
    apptNum: '',
    apptNote: '',
    apptRequired: false,
    additionalReferences: [],
  };
}

export function normalizeTurvoRouteStopTypes(
  stops: LoadStopsItem[]
): LoadStopsItem[] {
  if (stops.length === 0) {
    return stops;
  }

  return stops.map((stop, index) => {
    let stopType: LoadStopsItemTypes;
    if (index === 0) {
      stopType = LoadStopsItemTypes.Pickup;
    } else if (index === stops.length - 1) {
      stopType = LoadStopsItemTypes.Dropoff;
    } else if (isTurvoDropoffStopType(stop.stopType)) {
      stopType = LoadStopsItemTypes.Dropoff;
    } else {
      stopType = LoadStopsItemTypes.Pickup;
    }

    return {
      ...stop,
      order: index,
      stopNumber: index,
      stopType,
    };
  });
}

export function syncTurvoRouteStopsToPickupConsignee(
  stops: LoadStopsItem[]
): Pick<NormalizedLoad, 'pickup' | 'consignee'> {
  const defaults = defaultTurvoPickupConsignee();

  if (stops.length < 2) {
    return defaults;
  }

  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  return {
    pickup: flatPickupFromRouteStop(firstStop, defaults.pickup),
    consignee: flatDropoffFromRouteStop(lastStop, defaults.consignee),
  };
}

function flatPickupFromRouteStop(
  stop: LoadStopsItem,
  base: NormalizedPickup
): NormalizedPickup {
  return {
    ...base,
    externalTMSID: stop.externalTMSID ?? '',
    name: stop.address?.name ?? '',
    addressLine1: stop.address?.addressLine1 ?? '',
    addressLine2: stop.address?.addressLine2 ?? '',
    city: stop.address?.city ?? '',
    state: stop.address?.state ?? '',
    zipCode: stop.address?.zip ?? '',
    country: stop.address?.country ?? '',
    contact: stop.contact ?? '',
    phone: stop.phone ?? '',
    email: stop.email ?? '',
    refNumber: stop.refNumber ?? '',
    apptRequired: stop.apptRequired ?? false,
    apptType: stop.apptType ?? '',
    apptStartTime: toMaybeDate(stop.apptStartTime),
    apptEndTime: toMaybeDate(stop.apptEndTime),
    apptNum: stop.apptNum ?? '',
    apptNote: stop.apptNote ?? '',
    timezone: stop.timezone ?? stop.address?.timezone ?? '',
    additionalReferences: stop.additionalReferences ?? [],
    readyTime: toMaybeDate(stop.readyTime),
    readyTimeEnd: toMaybeDate(stop.readyEndTime),
  };
}

function flatDropoffFromRouteStop(
  stop: LoadStopsItem,
  base: NormalizedDropoff
): NormalizedDropoff {
  return {
    ...base,
    externalTMSID: stop.externalTMSID ?? '',
    name: stop.address?.name ?? '',
    addressLine1: stop.address?.addressLine1 ?? '',
    addressLine2: stop.address?.addressLine2 ?? '',
    city: stop.address?.city ?? '',
    state: stop.address?.state ?? '',
    zipCode: stop.address?.zip ?? '',
    country: stop.address?.country ?? '',
    contact: stop.contact ?? '',
    phone: stop.phone ?? '',
    email: stop.email ?? '',
    refNumber: stop.refNumber ?? '',
    apptRequired: stop.apptRequired ?? false,
    apptType: stop.apptType ?? '',
    apptStartTime: toMaybeDate(stop.apptStartTime),
    apptEndTime: toMaybeDate(stop.apptEndTime),
    apptNum: stop.apptNum ?? '',
    apptNote: stop.apptNote ?? '',
    timezone: stop.timezone ?? stop.address?.timezone ?? '',
    additionalReferences: stop.additionalReferences ?? [],
    mustDeliver: toMaybeDate(stop.mustDeliver),
    mustDeliverEnd: toMaybeDate(stop.readyEndTime),
  };
}

/**
 * Update mode equivalent of syncTurvoRouteStopsToPickupConsignee. Turvo's update
 * path writes the flat pickup/consignee, so route-end edits have to land there.
 * Location and address fields fall back to the saved load because Turvo's
 * globalRoute payload omits them, while the appointment and reference fields the
 * route form owns are taken verbatim so clearing one actually persists.
 */
export function mergeTurvoRouteEndsIntoPickupConsignee(
  stops: LoadStopsItem[],
  pickup: NormalizedPickup,
  consignee: NormalizedDropoff
): Pick<NormalizedLoad, 'pickup' | 'consignee'> {
  if (stops.length < 2) {
    return { pickup, consignee };
  }

  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  return {
    pickup: {
      ...pickup,
      ...routeEndLocationFields(firstStop, pickup),
      ...routeEndAppointmentFields(firstStop),
      readyTime: toMaybeDate(firstStop.readyTime),
      readyTimeEnd: toMaybeDate(firstStop.readyEndTime) ?? pickup.readyTimeEnd,
    },
    consignee: {
      ...consignee,
      ...routeEndLocationFields(lastStop, consignee),
      ...routeEndAppointmentFields(lastStop),
      mustDeliver: toMaybeDate(lastStop.mustDeliver),
      mustDeliverEnd:
        toMaybeDate(lastStop.readyEndTime) ?? consignee.mustDeliverEnd,
    },
  };
}

/** Fields Turvo's route payload omits, so the saved load stands in for blanks. */
function routeEndLocationFields(
  stop: LoadStopsItem,
  base: NormalizedPickup | NormalizedDropoff
) {
  return {
    externalTMSID: stop.externalTMSID?.trim() || base.externalTMSID,
    name: stop.address?.name?.trim() || base.name,
    addressLine1: stop.address?.addressLine1?.trim() || base.addressLine1,
    addressLine2: stop.address?.addressLine2?.trim() || base.addressLine2,
    city: stop.address?.city?.trim() || base.city,
    state: stop.address?.state?.trim() || base.state,
    zipCode: stop.address?.zip?.trim() || base.zipCode,
    country: stop.address?.country?.trim() || base.country,
    contact: stop.contact?.trim() || base.contact,
    phone: stop.phone?.trim() || base.phone,
    email: stop.email?.trim() || base.email,
    timezone: stop.timezone?.trim() || base.timezone,
  };
}

/** Fields the route form owns; a cleared value has to survive the merge. */
function routeEndAppointmentFields(stop: LoadStopsItem) {
  return {
    refNumber: stop.refNumber ?? '',
    apptRequired: stop.apptRequired ?? false,
    apptType: stop.apptType ?? '',
    apptNum: stop.apptNum ?? '',
    apptNote: stop.apptNote ?? '',
    apptStartTime: toMaybeDate(stop.apptStartTime),
    apptEndTime: toMaybeDate(stop.apptEndTime),
  };
}

/**
 * Hydration counterpart to mergeTurvoRouteEndsIntoPickupConsignee. Turvo's
 * globalRoute payload carries no address and no appointment flags, so a saved
 * route arrives sparse while the flat pickup/consignee hold what the UI renders.
 * Each end keeps its own values and falls back to the flat location for the rest.
 *
 * Form load only: running this on submit would refill fields the user just
 * cleared from the stale flat values, and the merge would write them back.
 */
export function hydrateTurvoRouteEndsFromPickupConsignee(
  stops: LoadStopsItem[],
  pickup: MaybeUndef<NormalizedPickup>,
  consignee: MaybeUndef<NormalizedDropoff>
): LoadStopsItem[] {
  if (stops.length < 2) {
    return stops;
  }

  const hydrated = [...stops];
  const lastIndex = hydrated.length - 1;
  hydrated[0] = hydrateRouteEndFromFlatLocation(hydrated[0], pickup);
  hydrated[lastIndex] = hydrateRouteEndFromFlatLocation(
    hydrated[lastIndex],
    consignee
  );

  return hydrated;
}

function hydrateRouteEndFromFlatLocation(
  stop: LoadStopsItem,
  location: MaybeUndef<NormalizedPickup | NormalizedDropoff>
): LoadStopsItem {
  if (!location) {
    return stop;
  }

  // Diverging IDs mean the two sides describe different facilities.
  const stopID = stop.externalTMSID?.trim() ?? '';
  const locationID = location.externalTMSID?.trim() ?? '';
  if (stopID && locationID && stopID !== locationID) {
    return stop;
  }

  const timezone = stop.timezone?.trim() || (location.timezone ?? '');

  return {
    ...stop,
    contact: stop.contact?.trim() || (location.contact ?? ''),
    phone: stop.phone?.trim() || (location.phone ?? ''),
    email: stop.email?.trim() || (location.email ?? ''),
    refNumber: stop.refNumber?.trim() || (location.refNumber ?? ''),
    apptNum: stop.apptNum?.trim() || (location.apptNum ?? ''),
    apptType: stop.apptType?.trim() || (location.apptType ?? ''),
    apptNote: stop.apptNote?.trim() || (location.apptNote ?? ''),
    apptRequired: stop.apptRequired || Boolean(location.apptRequired),
    timezone,
    address: {
      ...stop.address,
      name: stop.address?.name?.trim() || (location.name ?? ''),
      addressLine1:
        stop.address?.addressLine1?.trim() || (location.addressLine1 ?? ''),
      addressLine2:
        stop.address?.addressLine2?.trim() || (location.addressLine2 ?? ''),
      city: stop.address?.city?.trim() || (location.city ?? ''),
      state: stop.address?.state?.trim() || (location.state ?? ''),
      zip: stop.address?.zip?.trim() || (location.zipCode ?? ''),
      country: stop.address?.country?.trim() || (location.country ?? ''),
      timezone: stop.address?.timezone?.trim() || timezone,
    },
  };
}

export function isTurvoMultiStopRoute(
  stops: MaybeUndef<LoadStopsItem[]>
): boolean {
  return (stops?.length ?? 0) > 2;
}

export function turvoStopHasRouteContent(
  stop: MaybeUndef<LoadStopsItem>
): boolean {
  if (!stop) {
    return false;
  }

  return Boolean(
    stop.externalTMSID?.trim() ||
    stop.refNumber?.trim() ||
    stop.address?.name?.trim() ||
    stop.address?.city?.trim() ||
    stop.address?.addressLine1?.trim() ||
    String(stop.readyTime ?? '').trim() ||
    String(stop.mustDeliver ?? '').trim()
  );
}

export function turvoFlatLocationHasRouteContent(
  location: MaybeUndef<NormalizedPickup | NormalizedDropoff>
): boolean {
  if (!location) {
    return false;
  }

  return Boolean(
    location.externalTMSID?.trim() ||
    location.refNumber?.trim() ||
    location.name?.trim() ||
    location.city?.trim() ||
    location.addressLine1?.trim() ||
    (location as NormalizedPickup).readyTime ||
    (location as NormalizedDropoff).mustDeliver
  );
}

export function turvoRouteEndsHaveContent(stops: LoadStopsItem[]): boolean {
  if (stops.length < 2) {
    return false;
  }

  return (
    turvoStopHasRouteContent(stops[0]) ||
    turvoStopHasRouteContent(stops[stops.length - 1])
  );
}

export function turvoRouteStopLabel(
  stops: LoadStopsItem[],
  index: number
): string {
  if (index <= 0) {
    return 'Pickup';
  }

  if (index >= stops.length - 1) {
    return 'Consignee';
  }

  const action = isTurvoDropoffStopType(stops[index]?.stopType)
    ? 'Dropoff'
    : 'Pickup';
  return `Stop ${index} · ${action}`;
}

/**
 * Maps a route position to the suggestion field that can back it. Load-building
 * suggestions only carry flat pickup/consignee locations, so intermediate stops
 * have no suggested location to create on submit.
 */
export function turvoRouteSuggestionStopType(
  index: number,
  stopCount: number
): Undef<LoadStopTypes> {
  if (index <= 0) {
    return LoadStopTypes.Pickup;
  }

  if (index >= stopCount - 1) {
    return LoadStopTypes.Consignee;
  }

  return undefined;
}

export function turvoRouteStopCollapsedSummary(
  stop: MaybeUndef<LoadStopsItem>
): string {
  if (!stop) {
    return '';
  }

  const name = stop.address?.name?.trim() ?? '';
  const city = stop.address?.city?.trim() ?? '';
  const state = stop.address?.state?.trim() ?? '';
  const place = [city, state].filter(Boolean).join(', ');

  if (name && place) {
    return `${name} · ${place}`;
  }

  return name || place;
}

function routeStopComparableDate(stop: LoadStopsItem): unknown {
  if (isTurvoDropoffStopType(stop.stopType)) {
    return stop.mustDeliver || stop.apptStartTime;
  }
  return stop.readyTime || stop.apptStartTime;
}

export function validateTurvoMonotonicRouteStopDates(
  stops: LoadStopsItem[]
): { ok: true } | { ok: false; message: string; pathIndex: number } {
  for (let i = 0; i < stops.length - 1; i++) {
    const currentStop = stops[i];
    const nextStop = stops[i + 1];
    const currentTime = routeStopComparableDate(currentStop);
    const nextTime = routeStopComparableDate(nextStop);

    const result = validateDeliveryOnOrAfterPickupDate(currentTime, nextTime);

    if (!result.ok) {
      return {
        ok: false,
        message: result.message,
        pathIndex: i + 1,
      };
    }
  }

  return { ok: true };
}

export function ensureTurvoRouteStops(values: {
  stops?: MaybeUndef<LoadStopsItem[]>;
  pickup?: MaybeUndef<NormalizedPickup>;
  consignee?: MaybeUndef<NormalizedDropoff>;
}): LoadStopsItem[] {
  const existing = values.stops ?? [];
  const hasIntermediateContent = existing
    .slice(1, -1)
    .some((stop) => turvoStopHasRouteContent(stop));

  // A saved route wins over the flat fields; the flat fields only rebuild the
  // route when the stops carry nothing worth keeping.
  const hasSavedRoute =
    existing.length > 2 ||
    (existing.length >= 2 &&
      (turvoRouteEndsHaveContent(existing) || hasIntermediateContent));

  if (
    !hasSavedRoute &&
    (turvoFlatLocationHasRouteContent(values.pickup) ||
      turvoFlatLocationHasRouteContent(values.consignee))
  ) {
    return buildTurvoStopsFromPickupAndConsignee(
      values.pickup,
      values.consignee
    );
  }

  if (existing.length >= 2) {
    return normalizeTurvoRouteStopTypes(existing);
  }

  return buildTurvoStopsFromPickupAndConsignee(values.pickup, values.consignee);
}

export function applySelectedLocationToTurvoRouteStop(
  stop: LoadStopsItem,
  location: {
    externalTMSID?: string;
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    contact?: string;
    phone?: string;
    email?: string;
    timezone?: string;
  }
): LoadStopsItem {
  return {
    ...stop,
    externalTMSID: location.externalTMSID ?? stop.externalTMSID,
    contact: location.contact ?? stop.contact,
    phone: location.phone ?? stop.phone,
    email: location.email ?? stop.email,
    timezone: location.timezone ?? stop.timezone,
    address: {
      ...stop.address,
      name: location.name ?? stop.address?.name ?? '',
      addressLine1: location.addressLine1 ?? stop.address?.addressLine1 ?? '',
      addressLine2: location.addressLine2 ?? stop.address?.addressLine2 ?? '',
      city: location.city ?? stop.address?.city ?? '',
      state: location.state ?? stop.address?.state ?? '',
      zip: location.zipCode ?? stop.address?.zip ?? '',
      country: location.country ?? stop.address?.country ?? '',
      timezone: location.timezone ?? stop.address?.timezone ?? '',
    },
  };
}
